import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import '@/assets/shop.css'
import { useAuth } from '@/hooks/useAuth'
import ProductCard from '@/components/Shop/ProductCard'
import PurchaseModal from '@/components/Shop/PurchaseModal'
import ShopCart from '@/components/Shop/ShopCart'
import { useNotification } from '@/hooks/useNotification'
import {
  fetchProducts,
  fetchUserOrders,
  purchaseProduct as purchaseProductRequest,
  type Product,
  type DeliveryInfo,
  type CartItem,
  productTotal,
} from '@/services/shopService'
import { ApiError } from '@/services/authService'

// Mock products for simulation mode
const MOCK_PRODUCTS: Product[] = [
  {
    id: '1',
    name: 'Allcom Premium Tričko',
    description: 'Exkluzivní tričko s logem Allcom. Vyrobeno z kvalitní bavlny.',
    price: 500,
    imageUrl: '',
    sizes: ['S', 'M', 'L', 'XL', 'XXL'],
    maxPerUser: 1,
    isActive: true,
    requiresDelivery: true,
    shippingCost: 100,
  },
  {
    id: '2',
    name: 'Allcom Mikina',
    description: 'Pohodlná mikina s kapucí a vyšitým logem.',
    price: 1200,
    imageUrl: '',
    sizes: ['S', 'M', 'L', 'XL'],
    maxPerUser: 1,
    isActive: true,
    requiresDelivery: true,
    shippingCost: 100,
  },
  {
    id: '3',
    name: 'Allcom Kšiltovka',
    description: 'Stylová kšiltovka s vyšitým logem Allcom.',
    price: 350,
    imageUrl: '',
    sizes: ['Univerzální'],
    maxPerUser: 1,
    isActive: true,
    requiresDelivery: true,
    shippingCost: 100,
  },
  {
    id: '4',
    name: 'Allcom Digitální Wallpaper Pack',
    description: 'Sada 10 exkluzivních wallpaperů pro desktop a mobil.',
    price: 150,
    imageUrl: '',
    sizes: ['Digitální'],
    maxPerUser: 1,
    isActive: true,
    requiresDelivery: false,
    shippingCost: 0,
  },
]

const SIMULATION_INITIAL_COINS = 3200

function ShopPage() {
  const { user, loading: authLoading, refetchUser } = useAuth()
  const { showNotification } = useNotification()
  const [cart, setCart] = useState<CartItem[]>([])
  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const [cartMessage, setCartMessage] = useState('')
  const [deliveryDraft, setDeliveryDraft] = useState<DeliveryInfo>()
  const [purchasedProducts, setPurchasedProducts] = useState<Set<string>>(new Set())
  const [simulationMode, setSimulationMode] = useState(false)
  const [mockUserCoins, setMockUserCoins] = useState(SIMULATION_INITIAL_COINS)
  const purchasePending = useRef(false)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [affordableOnly, setAffordableOnly] = useState(false)
  const [purchaseMessage, setPurchaseMessage] = useState('')
  const purchaseStatus = useRef<HTMLParagraphElement>(null)
  const [remoteProducts, setRemoteProducts] = useState<Product[]>([])
  const [productsLoading, setProductsLoading] = useState(true)
  const [productsError, setProductsError] = useState<string | null>(null)
  const [isProcessingPurchase, setIsProcessingPurchase] = useState(false)
  const simulationFeatureEnabled =
    import.meta.env.VITE_ENABLE_SHOP_SIMULATION === 'true' ||
    import.meta.env.VITE_ENABLE_AUTH_SIMULATION === 'true'
  const userId = user?.id
  const formattedSimulationCoins = new Intl.NumberFormat('cs-CZ').format(SIMULATION_INITIAL_COINS)

  useEffect(() => {
    setCart([])
    setCheckoutOpen(false)
    setDeliveryDraft(undefined)
    setCartMessage('')
  }, [userId])

  useEffect(() => {
    if (purchaseMessage && !authLoading) purchaseStatus.current?.focus()
  }, [purchaseMessage, authLoading])

  useEffect(() => {
    if (userId === undefined || simulationMode) return

    let cancelled = false
    setProductsLoading(true)
    setProductsError(null)

    fetchProducts({ activeOnly: true })
      .then((items) => {
        if (!cancelled) {
          setRemoteProducts(items)
        }
      })
      .catch((error) => {
        if (cancelled) return
        console.error('Failed to load products:', error)
        const message =
          error instanceof ApiError
            ? error.message
            : error instanceof Error
              ? error.message
              : 'Nepodařilo se načíst produkty.'
        setProductsError(message)
      })
      .finally(() => {
        if (!cancelled) {
          setProductsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [userId, simulationMode, loadAttempt])

  useEffect(() => {
    if (userId === undefined || simulationMode) return

    let cancelled = false

    fetchUserOrders(userId)
      .then((orders) => {
        if (cancelled) return
        const purchased = new Set<string>(orders.map((order) => order.productId))
        setPurchasedProducts(purchased)
      })
      .catch((error) => {
        console.error('Failed to load user orders:', error)
      })

    return () => {
      cancelled = true
    }
  }, [userId, simulationMode, loadAttempt])

  // Check for simulation mode in URL or localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return

    if (!simulationFeatureEnabled) {
      setSimulationMode(false)
      localStorage.removeItem('shopSimulation')
      const url = new URL(window.location.href)
      if (url.searchParams.has('simulation')) {
        url.searchParams.delete('simulation')
        window.history.replaceState({}, '', url.toString())
      }
      return
    }

    const urlParams = new URLSearchParams(window.location.search)
    const simMode =
      urlParams.get('simulation') === 'true' || localStorage.getItem('shopSimulation') === 'true'
    setSimulationMode(simMode)
    if (simMode) {
      setMockUserCoins(SIMULATION_INITIAL_COINS)
      setPurchasedProducts(new Set())
    }
  }, [simulationFeatureEnabled])

  const handlePurchase = async (deliveryInfo?: DeliveryInfo) => {
    if (purchasePending.current || !cart.length) return
    purchasePending.current = true
    setIsProcessingPurchase(true)
    try {
      if (simulationMode) {
        const total = cart.reduce((sum, { product }) => sum + productTotal(product), 0)
        if (mockUserCoins < total) throw new Error('Na tento nákup nemáš dost mincí.')
        await new Promise((resolve) => setTimeout(resolve, 500))
        setMockUserCoins((coins) => coins - total)
        setPurchaseMessage('Ukázkový nákup je hotový. Žádná skutečná objednávka nevznikla.')
      } else {
        const result = await purchaseProductRequest({
          items: cart.map(({ product, size }) => ({ productId: product.id, size })),
          expectedTotal: cart.reduce((sum, { product }) => sum + productTotal(product), 0),
          deliveryInfo,
        })
        if (!result.success || result.orders?.length !== cart.length)
          throw new Error(result.message || 'Nákup se nezdařil. Zkus to znovu.')
        setPurchaseMessage('Celý nákup je hotový. Zakoupené produkty najdeš ve svém profilu.')
        showNotification('success', 'Nákup úspěšný', 'Všechny produkty z košíku jsou objednané.')
      }
      setPurchasedProducts(
        (current) => new Set([...current, ...cart.map(({ product }) => product.id)]),
      )
      setCart([])
      setCartMessage('')
      setCheckoutOpen(false)
      setDeliveryDraft(undefined)
      if (!simulationMode) await refetchUser()
    } finally {
      purchasePending.current = false
      setIsProcessingPurchase(false)
    }
  }

  const resetSimulationState = () => {
    setMockUserCoins(SIMULATION_INITIAL_COINS)
    setPurchasedProducts(new Set())
    setCart([])
    setCartMessage('')
    setCheckoutOpen(false)
    setDeliveryDraft(undefined)
    setPurchaseMessage('')
  }

  const startSimulation = () => {
    if (!simulationFeatureEnabled || typeof window === 'undefined') return
    resetSimulationState()
    setSimulationMode(true)
    localStorage.setItem('shopSimulation', 'true')
    const url = new URL(window.location.href)
    url.searchParams.set('simulation', 'true')
    window.history.replaceState({}, '', url.toString())
    setProductsError(null)
    showNotification(
      'info',
      'Simulace obchodu aktivní',
      'Nakupujete v demo režimu s fiktivními mincemi.',
    )
  }

  const stopSimulation = () => {
    if (typeof window === 'undefined') return
    setSimulationMode(false)
    localStorage.removeItem('shopSimulation')
    const url = new URL(window.location.href)
    if (url.searchParams.has('simulation')) {
      url.searchParams.delete('simulation')
      window.history.replaceState({}, '', url.toString())
    }
    resetSimulationState()
    showNotification('info', 'Simulace ukončena', 'Vracíme vás zpět k aktuální nabídce obchodu.')
    if (user) {
      void refetchUser()
    }
  }

  const products = simulationMode ? MOCK_PRODUCTS : remoteProducts
  const userCoins = simulationMode ? mockUserCoins : user?.economyStats?.coinsCount || 0
  const isLoading = authLoading || (productsLoading && !simulationMode)
  const visibleProducts = products.filter(
    (product) => !affordableOnly || productTotal(product) <= userCoins,
  )

  return (
    <div className="ac-shop ac-width">
      <header className="ac-shop-heading">
        <div>
          <h1>Allcom obchod</h1>
          <p>Vyměň mince za něco z aktuální nabídky. Cenu včetně dopravy vidíš rovnou.</p>
        </div>
        <dl className="ac-shop-balance">
          <dt>{simulationMode ? 'Ukázkový zůstatek' : 'Tvůj zůstatek'}</dt>
          <dd>
            <strong>{authLoading ? '—' : userCoins.toLocaleString('cs-CZ')}</strong> mincí
          </dd>
        </dl>
        <a className="ac-shop-button ac-shop-button-secondary" href="#shop-cart-title">
          Košík · {cart.length}
        </a>
      </header>

      {simulationFeatureEnabled && (
        <aside className="ac-shop-demo">
          <div>
            <strong>{simulationMode ? 'Ukázka obchodu' : 'Vyzkoušet obchod'}</strong>
            <p>
              {simulationMode
                ? 'Produkty, mince i objednávky jsou pouze ukázkové.'
                : 'Demo používá ' +
                  formattedSimulationCoins +
                  ' fiktivních mincí. Skutečné objednávky se nezmění.'}
            </p>
          </div>
          <button
            type="button"
            className="ac-shop-button ac-shop-button-secondary"
            disabled={isProcessingPurchase}
            onClick={simulationMode ? stopSimulation : startSimulation}
          >
            {simulationMode ? 'Ukončit ukázku' : 'Spustit ukázku'}
          </button>
        </aside>
      )}

      {purchaseMessage && (
        <p ref={purchaseStatus} role="status" tabIndex={-1} className="ac-shop-alert">
          {purchaseMessage}
        </p>
      )}
      <p className="ac-cart-status" role="status">
        {cartMessage}
      </p>
      <section aria-labelledby="shop-catalogue-title" aria-busy={isLoading}>
        <div className="ac-shop-toolbar">
          <h2 id="shop-catalogue-title">
            Aktuální nabídka
            {!isLoading && !productsError && <span> · {visibleProducts.length}</span>}
          </h2>
          <label>
            <input
              type="checkbox"
              checked={affordableOnly}
              onChange={(event) => setAffordableOnly(event.target.checked)}
            />
            Jen to, na co mám mince
          </label>
        </div>
        {isLoading ? (
          <div className="ac-shop-loading" role="status">
            <p>Načítáme nabídku…</p>
            <div className="ac-shop-catalogue" aria-hidden="true">
              {[0, 1, 2].map((item) => (
                <div key={item} className="ac-shop-skeleton" />
              ))}
            </div>
          </div>
        ) : productsError && !simulationMode ? (
          <div className="ac-shop-state" role="alert">
            <h3>Nabídku se nepodařilo načíst.</h3>
            <p>{productsError}</p>
            <button
              type="button"
              className="ac-shop-button"
              onClick={() => setLoadAttempt((attempt) => attempt + 1)}
            >
              Zkusit znovu
            </button>
          </div>
        ) : visibleProducts.length === 0 ? (
          <div className="ac-shop-state" role="status">
            <h3>
              {products.length > 0 ? 'Na tyhle produkty ještě šetříš.' : 'Nabídka je teď prázdná.'}
            </h3>
            <p>
              {products.length > 0
                ? 'Zobraz si celou nabídku a podívej se, kolik mincí ti chybí.'
                : 'Zastav se později. Mezitím se potkáme na Discordu.'}
            </p>
            {products.length > 0 ? (
              <button
                type="button"
                className="ac-shop-button ac-shop-button-secondary"
                onClick={() => setAffordableOnly(false)}
              >
                Zobrazit celou nabídku
              </button>
            ) : (
              <a
                className="ac-text-link"
                href="https://discord.allcom.zone"
                target="_blank"
                rel="noopener noreferrer"
              >
                Přejít na Discord
              </a>
            )}
          </div>
        ) : (
          <div className="ac-shop-catalogue">
            {visibleProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                inCart={cart.some((item) => item.product.id === product.id)}
                onAdd={(product, size) => {
                  setPurchaseMessage('')
                  setCart((current) =>
                    current.some((item) => item.product.id === product.id)
                      ? current
                      : [...current, { product, size }],
                  )
                  setCartMessage(`${product.name} je v košíku. Můžeš vybírat dál.`)
                }}
                isPurchased={purchasedProducts.has(product.id)}
                userCoins={userCoins}
                isLoading={isProcessingPurchase}
              />
            ))}
          </div>
        )}
      </section>
      <ShopCart
        items={cart}
        userCoins={userCoins}
        busy={isProcessingPurchase}
        onRemove={(productId) => {
          setCart((current) => current.filter(({ product }) => product.id !== productId))
          setCartMessage('Produkt byl odebrán z košíku.')
        }}
        onSizeChange={(productId, size) =>
          setCart((current) =>
            current.map((item) => (item.product.id === productId ? { ...item, size } : item)),
          )
        }
        onCheckout={() => setCheckoutOpen(true)}
      />
      <div className="ac-shop-help">
        <p>
          Platíš komunitními mincemi. Vyber produkty a varianty, pak dokonči celý nákup z košíku.
        </p>
        <Link to="/profile" className="ac-text-link">
          Můj profil
        </Link>
      </div>
      {checkoutOpen && (
        <PurchaseModal
          items={cart}
          userCoins={userCoins}
          onClose={() => {
            if (!isProcessingPurchase) setCheckoutOpen(false)
          }}
          onConfirm={handlePurchase}
          deliveryDraft={deliveryDraft}
          onDeliveryChange={setDeliveryDraft}
          isLoading={isProcessingPurchase}
        />
      )}
    </div>
  )
}

export default ShopPage
